import 'swiper/css';

import {
  EIconButtonSize,
  EIconButtonVariant,
  EModalVariants,
  ETextStyleVariant,
  GeneralArrowLeftIcon,
  GeneralArrowRightIcon,
  GeneralCloseIcon,
  IconButton,
  Modal,
  Text,
} from '@outdoorsyco/bonfire';
import React, { Children, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Swiper as SwiperObj } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

type TLightboxProps = {
  onClose: () => void;
  initialSlide?: number;
  className?: string;
  onSlideChange?: (index: number) => void;
  children: React.ReactNode;
};

export const Lightbox = ({
  onClose,
  initialSlide = 0,
  className = '',
  onSlideChange,
  children,
}: TLightboxProps) => {
  const intl = useIntl();

  const [swiperInstance, setSwiperInstance] = useState<SwiperObj>();
  const [activeImageIndex, setActiveImageIndex] = useState(initialSlide);

  const slides = Children.toArray(children);

  useEffect(() => {
    const changeActiveSlide = (e: KeyboardEvent) => {
      if (!swiperInstance) return;

      if (e.code === 'ArrowLeft') {
        swiperInstance.slidePrev();
      }

      if (e.code === 'ArrowRight') {
        swiperInstance.slideNext();
      }
    };

    document.addEventListener('keydown', changeActiveSlide);

    return () => {
      document.removeEventListener('keydown', changeActiveSlide);
    };
  }, [swiperInstance]);

  const handlePrev = () => {
    swiperInstance?.slidePrev();
  };

  const handleNext = () => {
    swiperInstance?.slideNext();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      variant={EModalVariants.Full}
      dialogClassName="bg-black/60"
      className={className}>
      <div className="w-full h-full lg:px-32">
        <Swiper
          loop
          slidesPerView={1}
          slidesPerGroup={1}
          centeredSlides={true}
          spaceBetween={16}
          initialSlide={activeImageIndex}
          onSwiper={swiper => setSwiperInstance(swiper)}
          onRealIndexChange={swiper => {
            setActiveImageIndex(swiper.realIndex);
            onSlideChange?.(swiper.realIndex);
          }}
          className="w-full h-full">
          {slides.map((slide, idx) => (
            <SwiperSlide key={idx}>{slide}</SwiperSlide>
          ))}
        </Swiper>

        {slides.length > 1 && (
          <div className="absolute left-0 right-0 justify-between hidden px-10 lg:flex top-1/2 -translate-y-1/2">
            <IconButton
              onClick={handlePrev}
              icon={GeneralArrowLeftIcon}
              size={EIconButtonSize.Medium}
              variant={EIconButtonVariant.Primary}
              aria-label={intl.formatMessage({ defaultMessage: 'Previous', id: 'JJNc3c' })}
            />

            <IconButton
              onClick={handleNext}
              icon={GeneralArrowRightIcon}
              size={EIconButtonSize.Medium}
              variant={EIconButtonVariant.Primary}
              aria-label={intl.formatMessage({ defaultMessage: 'Next', id: '9+Ddtu' })}
            />
          </div>
        )}

        {slides.length > 1 && (
          <div className="absolute left-0 right-0 px-24 text-center top-8 py-0.5 z-9">
            <Text variant={ETextStyleVariant.MediumBold} className="text-white">
              {intl.formatMessage(
                {
                  id: 'xo5f8Q',
                  defaultMessage: '{current} of {total}',
                  description: 'Lightbox > image position counter (e.g. "1 of 3")',
                },
                { current: activeImageIndex + 1, total: slides.length },
              )}
            </Text>
          </div>
        )}

        <div className="absolute top-0 right-0 z-10 p-6">
          <IconButton
            onClick={onClose}
            icon={GeneralCloseIcon}
            size={EIconButtonSize.Medium}
            variant={EIconButtonVariant.Transparent}
            aria-label={intl.formatMessage({ defaultMessage: 'Close', id: 'rbrahO' })}
            className="brightness-0 invert"
          />
        </div>
      </div>
    </Modal>
  );
};
